import { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import { logout } from "../../store/slices/AuthSlice";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import CustomAppBar from "./CustomAppBar";
import DrawerContent from "./DrawerContent";
import AppRoutes from "../routes/Routes";
import ConfirmationDialog from "../common11/commonModals/ConfirmationDilouge";
import { getScreenNameForPath, recordScreenVisit } from "../../utils/recentActivity";

const TRIPLE_CLICK_MS = 500;

export default function Layout() {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const [isOpenConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef(null);

  const handleOpenDialog = () => setOpenConfirmationDialog(true);
  const handleCloseDialog = () => setOpenConfirmationDialog(false);
  const handleConfirm = () => {
    dispatch(logout());
    handleCloseDialog();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    setIsHovered(!mobileOpen);
  };

  const handleMenuItemClick = () => {
    if (mobileOpen) setMobileOpen(false);
  };

  const handleMainContentClick = useCallback(() => {
    if (isDrawerOpen) return;
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickCountRef.current += 1;
    clickTimerRef.current = setTimeout(() => {
      if (clickCountRef.current >= 3) {
        setIsDrawerOpen(true);
      }
      clickCountRef.current = 0;
      clickTimerRef.current = null;
    }, TRIPLE_CLICK_MS);
  }, [isDrawerOpen]);

  useEffect(() => {
    const screenName = getScreenNameForPath(location.pathname);
    if (screenName) recordScreenVisit(location.pathname, screenName);
  }, [location.pathname]);

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        flexDirection: "column",
        background: "#F4F9FD",
      }}
    >
      <CssBaseline />
      <CustomAppBar
        handleDrawerToggle={handleDrawerToggle}
        handleOpenDialog={handleOpenDialog}
        isLoggedIn={isLoggedIn}
        mobileOpen={mobileOpen}
        onAreaClick={handleMainContentClick}
      />
      {isLoggedIn && (
        <DrawerContent
          mobileOpen={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
          handleMenuItemClick={handleMenuItemClick}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          isHovered={isHovered}
          setIsHovered={setIsHovered}
          isLargeScreen={isLargeScreen}
        />
      )}
      <Box
        component="main"
        className="hims-main-content hims-page-root"
        onDoubleClick={() => isDrawerOpen && setIsDrawerOpen(false)}
        sx={{
          flexGrow: 1,
          marginTop: "56px",
          marginLeft: {
            xs: 0,
            lg: isDrawerOpen ? "18rem" : "0rem",
          },
          transition: "margin-left 0.3s ease-in-out",
          zIndex: 30,
          minWidth: 0,
        }}
      >
        <AppRoutes />
        {isOpenConfirmationDialog && (
          <ConfirmationDialog
            isOpen={isOpenConfirmationDialog}
            onClose={handleCloseDialog}
            onConfirm={handleConfirm}
            message="Are you sure you want to logout?"
            showSvg
          />
        )}
      </Box>
    </Box>
  );
}
